"use client";

import Link from 'next/link';
import React, { useState, useRef, useEffect, Suspense } from 'react';
import { X, Sparkles, ArrowRight, Store, ShieldCheck } from 'lucide-react';
import { CompanySlider, Merchant, OffersOffer, SearchProduct, } from '@/services/dataTypes';
import { keywordsAction, productSearchAction } from '@/app/actions';
import { getMerchantHref, getProductModalHref } from '@/constants/hooks';
import { FontAwesomeIcon } from '@/constants/icons';
import { faStore, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { useSearchParams } from 'next/navigation';

/* Types */
interface Props {
    keywords: string[];
    mer_slug: string;
    cat_slug: string;
    companyId: string;
    domain: string;
    sliders: CompanySlider[];
    popularOffers: OffersOffer[];
    whatsapp: string | null;
    mer_slug_type?: string;
    headerPromoMerchant?: Merchant[] | null;
}
interface SearchPanelProps {
    companyId: string;
    mer_slug: string;
    slug_type: string;
    popularTags: string[];
}

const FALLBACK_TAGS = [
    'Best VPN for Streaming', 'Compare iPhone vs Samsung',
    'Cheap flights to Dubai', 'Best Nike deals today',
    'WordPress hosting', 'Best protein for beginners',
];

/* Search Panel - Products Only */
const HeroSearchPanel = ({ companyId, mer_slug, slug_type, popularTags }: SearchPanelProps) => {
    const searchParams = useSearchParams();

    const [search, setSearch] = useState(() => new URLSearchParams(searchParams.toString()).get('query') || '');
    const [dropOpen, setDropOpen] = useState(false);
    const [focused, setFocused] = useState(false);
    const [tags, setTags] = useState<string[]>(popularTags ?? []);
    const [products, setProducts] = useState<SearchProduct[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const inputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    /* load keyword tags once */
    useEffect(() => {
        if (!tags.length) {
            keywordsAction(companyId).then(r => {
                if (r?.data) setTags(r.data);
            });
        }
    }, [companyId, tags.length]);

    /* debounced search */
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!search.trim()) {
                setProducts([]);
                setError('');
                setDropOpen(false);
                return;
            }
            if (search.trim().length >= 3) {
                searchProducts(search.trim());
            } else {
                setError('Type at least 3 characters');
                setProducts([]);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [search]);

    /* close on outside click */
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setDropOpen(false);
                setFocused(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const searchProducts = async (term: string) => {
        setLoading(true);
        setError('');
        try {
            const res = await productSearchAction(term, companyId);

            // Handle different response structures
            let productsData: SearchProduct[] = [];

            if (res?.data?.data && Array.isArray(res.data.data)) {
                productsData = res.data.data;
            } else if (res?.data && Array.isArray(res.data)) {
                productsData = res.data;
            } else if (Array.isArray(res)) {
                productsData = res;
            }

            if (productsData.length > 0) {
                setProducts(productsData);
                setDropOpen(true);
            } else {
                setProducts([]);
                setError('No products found');
                setDropOpen(true);
            }
        } catch (err) {
            console.error('Search error:', err);
            setError('Search failed. Please try again.');
            setProducts([]);
            setDropOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setSearch('');
        setProducts([]);
        setError('');
        setDropOpen(false);
        inputRef.current?.focus();
    };

    const showDropdown = dropOpen && search.trim().length >= 3;
    const displayTags = tags.length > 0 ? tags.slice(0, 6) : FALLBACK_TAGS;

    return (
        <div ref={wrapperRef} className="w-full flex flex-col">
            {/*  Search Input */}
            <div className={`flex items-center gap-2 w-full border rounded-2xl px-4 py-3 transition-all duration-300 ${focused
                ? 'border-[#1E3A5F] bg-white shadow-[0_0_0_4px_rgba(30,58,95,0.08)]'
                : 'border-[#E5E7EB] bg-[#F8FAFC] hover:border-[#1E3A5F]/40 hover:bg-white'
                }`}>
                <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className={`w-4 h-4 shrink-0 transition-colors duration-200 ${focused ? 'text-[#F97316]' : 'text-[#4B5563]'}`}
                />
                <input
                    ref={inputRef}
                    type="text"
                    value={search}
                    placeholder="Search for products, deals, stores..."
                    className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-[#111827] placeholder-[#4B5563] font-medium"
                    onChange={e => setSearch(e.target.value)}
                    onFocus={() => {
                        setFocused(true);
                        if (search.trim().length >= 3 && products.length > 0) {
                            setDropOpen(true);
                        }
                    }}
                    onKeyDown={e => {
                        if (e.key === 'Escape') {
                            setDropOpen(false);
                            setFocused(false);
                            inputRef.current?.blur();
                        }
                    }}
                    aria-label="Search for products"
                />
                {search.trim().length > 0 && (
                    <button
                        onClick={handleClear}
                        aria-label="Clear search"
                        className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-[#E5E7EB] hover:bg-[#1E3A5F] text-[#4B5563] hover:text-white transition-all cursor-pointer"
                    >
                        <X size={13} />
                    </button>
                )}
            </div>

            {/* ── Results / Tags area ─────────────────────────────────── */}
            <div className="mt-3">
                {showDropdown ? (
                    /* ── Product Results Dropdown ── */
                    <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_8px_40px_-8px_rgba(30,58,95,0.18)] overflow-hidden">
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#F8FAFC] border-b border-[#E5E7EB]">
                            <span className="text-[10px] font-black uppercase tracking-widest text-[#4B5563]">
                                {loading ? 'Searching...' : 'Products'}
                            </span>
                        </div>

                        {/* Results Body */}
                        <div className="overflow-y-auto overscroll-contain p-4" style={{ maxHeight: '320px' }}>
                            {/* Loading State */}
                            {loading && (
                                <div className="flex items-center justify-center gap-3 py-6">
                                    <div className="relative w-7 h-7 shrink-0">
                                        <div className="absolute inset-0 rounded-full border-2 border-[#E5E7EB]" />
                                        <div className="absolute inset-0 rounded-full border-2 border-[#F97316] border-t-transparent animate-spin" />
                                    </div>
                                    <p className="text-[12px] text-[#4B5563] font-medium">
                                        Searching for <span className="text-[#1E3A5F] font-bold">&ldquo;{search}&rdquo;</span>
                                    </p>
                                </div>
                            )}

                            {/* Error State */}
                            {error && !loading && (
                                <div className="px-3 py-2.5 rounded-xl text-[12px] font-semibold bg-red-50 text-[#DC2626] text-center">
                                    {error}
                                </div>
                            )}

                            {/* Products List */}
                            {products.length > 0 && !loading && (
                                <div className="space-y-2">
                                    {products.slice(0, 8).map(product => {
                                        const merchantHref = getMerchantHref(product.merchant, mer_slug, slug_type);
                                        const productUrl = getProductModalHref(merchantHref, product.unique_id);

                                        return (
                                            <Link
                                                key={product.unique_id}
                                                href={productUrl}
                                                onClick={() => setDropOpen(false)}
                                                className="w-full flex items-center justify-between gap-3 px-3 py-2.5 border border-[#E5E7EB] rounded-xl bg-white hover:bg-[#F8FAFC] hover:border-[#1E3A5F]/30 transition-all duration-200 group cursor-pointer no-underline"
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-[13px] font-semibold text-[#111827] group-hover:text-[#1E3A5F] line-clamp-1 transition-colors mb-1">
                                                        {product.title}
                                                    </p>
                                                    <div className="flex items-center gap-1.5">
                                                        <FontAwesomeIcon icon={faStore} className="w-2.5 h-2.5 text-[#4B5563] shrink-0" />
                                                        <span className="text-[11px] text-[#4B5563] group-hover:text-[#1E3A5F] font-medium transition-colors line-clamp-1">
                                                            {product.merchant.merchant_name}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="shrink-0 flex flex-col items-end gap-0.5">
                                                    <span className="text-[13px] font-black text-[#15803D]">
                                                        ${Number(product.sale_price).toFixed(2)}
                                                    </span>
                                                    <ArrowRight size={14} className="text-[#4B5563] group-hover:text-[#F97316] transition-colors" />
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            )}

                            {/* No Results State */}
                            {!loading && !error && products.length === 0 && (
                                <div className="px-3 py-2.5 rounded-xl text-[12px] font-semibold bg-gray-50 text-[#4B5563] text-center">
                                    Start typing to search for products...
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    /* ── Popular Search Tags ── */
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-2.5">
                            Try these popular searches
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {displayTags.map((tag, i) => (
                                <button
                                    key={i}
                                    onClick={() => {
                                        setSearch(tag);
                                        inputRef.current?.focus();
                                    }}
                                    className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#4B5563] bg-white border border-[#E5E7EB] hover:border-[#F97316] hover:text-[#EA580C] hover:bg-[#FFF7ED] px-3.5 py-1.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap group"
                                >
                                    {tag}
                                    <ArrowRight size={10} className="text-[#4B5563] group-hover:text-[#F97316] shrink-0 transition-colors" />
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

/* Main Hero Section */
const HeroSection = ({
    keywords, sliders, whatsapp,
    mer_slug, cat_slug, companyId,
    mer_slug_type = 'slug',
    headerPromoMerchant,
}: Props) => {

    return (
        <section className="relative w-full flex flex-col font-sans">
            {/* Hero body */}
            <div className="relative w-full bg-[#F8FAFC] border-b border-[#E5E7EB]">

                {/* background blobs */}
                <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#F97316]/5 rounded-full blur-[80px] translate-x-1/3 -translate-y-1/3" />
                    <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#1E3A5F]/4 rounded-full blur-[80px] -translate-x-1/3 translate-y-1/3" />
                </div>

                <div className="relative container mx-auto px-6 sm:px-8 lg:px-12 py-16 lg:py-20 pt-32 lg:pt-36 [body:has(#t20-announcement-bar)_&]:pt-[9.5rem] [body:has(#t20-announcement-bar)_&]:lg:pt-[10rem]">
                    <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16 xl:gap-20">

                        {/* ═══ LEFT — copy + stats ═══ */}
                        <div className="w-full lg:w-[45%] shrink-0">

                            {/* eyebrow label */}
                            <div className="inline-flex items-center gap-2 bg-[#F8FAFC] border border-[#1E3A5F]/15 text-[#1E3A5F] rounded-full px-4 py-1.5 mb-6">
                                <Sparkles size={11} className="text-[#F97316] shrink-0" />
                                <span className="text-[11px] font-black uppercase tracking-widest">AI Shopping Concierge</span>
                            </div>

                            {/* headline */}
                            <h1 className="font-extrabold leading-[1.1] tracking-tight text-[#1E3A5F] mb-5"
                                style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)' }}>
                                Find Daily Deals,
                                {/* shopping for{' '} */}
                                <span className="text-[#F97316]"> Exclusive Coupons & Verified Price Drops</span>
                            </h1>

                            {/* body */}
                            <p className="text-[#4B5563] text-[15px] leading-[1.7] mb-4 max-w-[420px]">
                                Save big on top brands with daily updated promo codes, instant discounts, and handpicked deals across fashion, tech, home, and more.
                            </p>
                        </div>

                        {/* ═══ RIGHT — AI search card ═══ */}
                        <div className="w-full lg:flex-1 self-start">
                            <div className="bg-white rounded-3xl border border-[#E5E7EB]
                                shadow-[0_8px_48px_-8px_rgba(30,58,95,0.14),0_2px_8px_-2px_rgba(30,58,95,0.06)]
                                overflow-hidden">

                                {/* card header gradient strip */}
                                <div className="h-1 w-full"
                                    style={{ background: 'linear-gradient(90deg,#1E3A5F 0%,#F97316 100%)' }} />

                                <div className="p-6 sm:p-7">

                                    {/* card title row */}
                                    <div className="flex items-center gap-3 mb-5">
                                        <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-[#1E3A5F] to-[#F97316] flex items-center justify-center shrink-0 shadow-md">
                                            <Sparkles size={15} className="text-white" />
                                        </div>
                                        <div>
                                            <p className="text-[13px] font-bold text-[#111827] leading-tight">
                                                Ask Savylo AI anything about shopping…
                                            </p>
                                            <p className="text-[11px] text-[#4B5563] mt-0.5 leading-tight">
                                                Deals · Coupons · Comparisons · Advice
                                            </p>
                                        </div>
                                    </div>

                                    {/* search + chips/results */}
                                    <Suspense fallback={
                                        <div className="space-y-3">
                                            <div className="h-12 w-full rounded-2xl bg-[#F8FAFC] animate-pulse" />
                                            <div className="h-24 w-full rounded-xl bg-[#F8FAFC] animate-pulse" />
                                        </div>
                                    }>
                                        <HeroSearchPanel
                                            companyId={companyId}
                                            mer_slug={mer_slug}
                                            slug_type={mer_slug_type}
                                            popularTags={keywords?.slice(0, 6) ?? []}
                                        />
                                    </Suspense>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* ── Featured Stores + Verified badge — full width, centered below both columns ── */}
                    {headerPromoMerchant && headerPromoMerchant.length > 0 && (
                            <div className="flex flex-wrap justify-center items-center gap-2">

                                {/* 100% Verified Coupons badge */}
                                <div className="inline-flex items-center gap-3 mr-2">
                                    <div className="w-10 h-10 rounded-2xl bg-[#DCFCE7] text-[#15803D] flex items-center justify-center shrink-0">
                                        <ShieldCheck size={16} />
                                    </div>
                                    <div>
                                        <p className="text-[14px] font-black text-[#111827] leading-none">100%</p>
                                        <p className="text-[11px] text-[#4B5563] font-medium mt-0.5 leading-none">Verified Coupons</p>
                                    </div>
                                </div>

                                {/* divider */}
                                <div className="w-px h-6 bg-[#E5E7EB] mx-1" />

                                {/* Store pills */}
                                {headerPromoMerchant.map((item, i) => (
                                    <Link
                                        key={i}
                                        href={getMerchantHref(item, mer_slug, mer_slug_type)}
                                        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#1E3A5F] bg-white border border-[#1E3A5F]/20 hover:border-[#F97316] hover:text-[#EA580C] hover:bg-[#FFF7ED] px-3 py-1.5 rounded-full transition-all duration-200 no-underline whitespace-nowrap group"
                                    >
                                        <Store size={11} className="shrink-0 text-[#4B5563] group-hover:text-[#F97316] transition-colors" />
                                        {item?.merchant_name}
                                    </Link>
                                ))}
                            </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default HeroSection;